import {FixedDataGrid, Row} from "../../../lib/sbhtml/fixed-data-grid"
import {Input} from "../../../lib/sbhtml/input"
import {SectionHeading} from "../../../lib/sbhtml/heading"
import {SingleBox as Select} from "../../../lib/sbhtml/select"
import * as React from "react"
import {validateDollars} from "../../../lib/price-utilities"
import {HelpButton} from "../../../lib/help button";
import * as strings from '../common/strings'
import {isAnnual, isSignupCost, isRenewCost} from "../common/utils";
import {ProrataPeriodInput} from "../common/presentation"
import {CostItem} from "./types";
import {InfoformField, Mode, GstStatus, ProrataPeriod, AnnualFormType, FinancialCodes} from "../common/types";

const heading = {
	CostItem: "Cost Item",
	Price: "Price",
	Mode: "Price Type",
	GstStatus: "GST Status",
	ProrataPeriod: "Pro-rata Period",
	Signup: "Signup",
	Renewal: "Renewal",
	ItemCode: "Item Code",
	JobCode: "Job Code",
	AccountNumber: "Account Number",
}
type Fields = typeof heading
class CostItemFixedDataGrid extends FixedDataGrid<Fields>{}

interface FieldSelectorProps {
	costItem:CostItem
	fields:InfoformField[]
	onFieldChange:(id:string, field:InfoformField) => void
}

const FieldSelector = (props:FieldSelectorProps) => {
	const options = props.fields.map((field) =>
		<option key={`costItem-${props.costItem.id}-field-${field.id}`} value={field.id.toString()}>
			{field.label}
		</option>
	)

	const onChange = (value:string) =>
		props.onFieldChange(
			props.costItem.id,
			props.fields.find((field) => field.id == parseInt(value))!)

	return <Select
		onChange={onChange}
		value={props.costItem.infoformField > 0 ? props.costItem.infoformField.toString() : undefined}
		name={`${props.costItem.id}InfoformField`}>
		{options}
	</Select>
}

interface PriceInputProps {
	costItem:CostItem
	fields:InfoformField[]

	onPriceChange:(id:string, value:string) => void
	onFieldChange:(id:string, field:InfoformField) => void
}

const PriceInput = (props:PriceInputProps) =>
	isAnnual(props.costItem) && props.costItem.mode == "infoform field" && props.fields.length > 0
		? <FieldSelector
			costItem={props.costItem}
			fields={props.fields}
			onFieldChange={props.onFieldChange} />
		: <Input
			size="tiny"
			value={validateDollars(props.costItem.price)}
			name={`${props.costItem.id}Price`}
			onChange={value => props.onPriceChange(props.costItem.id, value)} />

interface ModeInputProps {
	costItem:CostItem
	fields:InfoformField[]
	autoRenewalEnabled:boolean

	onChange: (value:Mode) => void
}

const cantUseInfoformsError = (costItem:CostItem, autoRenewEnabled:boolean, fields:InfoformField[]) => {
	
	if (autoRenewEnabled && isRenewCost(costItem)) {
		return strings.cantUseFieldsDueToAutoRenewal
	} else if (fields.length == 0) {
		const type:AnnualFormType = isSignupCost(costItem) ? "signup" : "renew"
		return strings.noFormFieldsWithPrices_requiresConfiguration(type)
	} else {
		return null
	}
}

const ModeInput = (props:ModeInputProps) => {

	const errorMessage         = cantUseInfoformsError(props.costItem, props.autoRenewalEnabled, props.fields)
	const canUseInfoformsField = errorMessage === null
	const fieldName            = `${props.costItem.id}Mode`
	const cantUseInfoformsJsx  = errorMessage === null ? null :
		<span>
			{' '}
			<HelpButton message={errorMessage} />
			<input type="hidden" name={fieldName} value={props.costItem.mode} />
		</span>

	return <div style={{whiteSpace: "nowrap"}}>
		<ModeInputSelectList
			fieldName={fieldName}
			value={canUseInfoformsField ? props.costItem.mode : "price"}
			onChange={props.onChange}
			enabled={canUseInfoformsField}
		/>
		{cantUseInfoformsJsx}
	</div>
}

interface ModeInputSelectListProps {
	fieldName:string
	value:Mode
	onChange: (value:Mode) => void
	enabled:boolean
}

const ModeInputSelectList = (props:ModeInputSelectListProps) =>
	<Select
		name={props.fieldName}
		value={props.value}
		onChange={value => props.onChange(value as Mode)}
		enabled={props.enabled}>

		<option value="price">Fixed</option>
		<option value="infoform field">Form Field</option>

	</Select>

const renderCostItem = (props: StandardCostItemProps) => (costItem: CostItem): Row<keyof Fields>=> {
	const infoformFields:InfoformField[]
		= !isAnnual(costItem) ? []
		: isSignupCost(costItem) ? props.SignupFields
		: props.RenewFields
	const infoformField = infoformFields.find(field => field.id == costItem.infoformField)

	const financialCodes:FinancialCodes = (costItem.mode == "infoform field" && infoformField != null) ? infoformField : costItem

	return {
		'CostItem': {
			alignment: "right",
			node: <div className="sbReadOnlyField sbTextInputMedium">{costItem.costItem}</div>
		},
		'Mode': {
			alignment: "left",
			node:
				!isAnnual(costItem) ? null :
					<ModeInput
						costItem={costItem}
						fields={infoformFields}
						autoRenewalEnabled={props.AutoRenewalEnabled}
						onChange={(mode) => mode == "price" ? props.onModeChangeToPrice(costItem.id) : props.onModeChangeToInfoformField(costItem.id, infoformFields[0])} />
		},
		'Price': {
			node:
				<PriceInput
					costItem={costItem}
					fields={infoformFields}
					onPriceChange={props.onPriceChange}
					onFieldChange={props.onPriceFieldChange} />
		},
		'GstStatus': {
			node:
				<Select
					value={costItem.gstStatus}
					onChange={value => props.onGstStatusChange(costItem.id, value as GstStatus)}
					name={`${costItem.id}GstStatus`}>

					<option value="type">Use Default</option>
					<option value="inclusive">GST Inclusive</option>
					<option value="exclusive">GST Exclusive</option>
					<option value="exempt">GST Exempt</option>

				</Select>
		},
		'ProrataPeriod': {
			node: !isAnnual(costItem) ? null :
				<ProrataPeriodInput
					value={costItem.prorataPeriod}
					onChange={period => props.onProrataPeriodChange(costItem.id, period)}
					name={`${costItem.id}ProrataPeriod`} />
		},
		'Signup': {node: <input type="checkbox" checked={costItem.signup} disabled={true} />},
		'Renewal': {node: <input type="checkbox" checked={costItem.renewal} disabled={true} />},
		'ItemCode': {
			node:
				<Input
					size="tiny"
					value={financialCodes.glCode}
					name={`${costItem.id}GlCode`}
					disabled={costItem.mode == "infoform field"}
					onChange={value => props.onItemCodeChange(costItem.id, value)} />
		},
		'JobCode': {
			node:
				<Input
					size="tiny"
					value={financialCodes.jobCode}
					name={`${costItem.id}JobCode`}
					disabled={costItem.mode == "infoform field"}
					onChange={value => props.onJobCodeChange(costItem.id, value)} />
		},
		'AccountNumber': {
			node:
				<Input
					size="tiny"
					value={financialCodes.accountNumber}
					name={`${costItem.id}AccountNumber`}
					disabled={costItem.mode == "infoform field"}
					onChange={value => props.onAccountNumberChange(costItem.id, value)} />
		}
	}
}

export interface DataProps {
	CostItems: CostItem[]
	SignupFields: InfoformField[]
	RenewFields: InfoformField[]
	AutoRenewalEnabled: boolean
	FinancialCodeLabels: FinancialCodes
}
export interface CallbackProps {
	onPriceChange: (id: string, price: string) => void
	onPriceFieldChange: (id: string, field: InfoformField) => void
	onModeChangeToPrice: (id: string) => void
	onModeChangeToInfoformField: (id: string, defaultField: InfoformField) => void
	onGstStatusChange: (id: string, gstStatus: GstStatus) => void
	onItemCodeChange: (id: string, itemCode: string) => void
	onJobCodeChange: (id: string, jobCode: string) => void
	onAccountNumberChange: (id: string, accountNumber: string) => void
	onProrataPeriodChange: (id: string, prorataPeriod: ProrataPeriod) => void
}

export type StandardCostItemProps = CallbackProps & DataProps

const CostItemList = (props:StandardCostItemProps) => {
	const myHeadings: Fields = {
		...heading,
		AccountNumber:
		props.FinancialCodeLabels.accountNumber,
		JobCode: props.FinancialCodeLabels.jobCode,
		ItemCode: props.FinancialCodeLabels.glCode
	}

	return <CostItemFixedDataGrid
		heading={myHeadings}
		data={
			props.CostItems.map(renderCostItem(props))
		}
	/>

}

export const StandardCostItems = (props:StandardCostItemProps) =>
	<div>
		<SectionHeading title="Standard Cost Items" />
		<CostItemList {...props} />
	</div>



// WEBPACK FOOTER //
// ./src/membership3/pricing-option-form/standard-items/presentation.tsx